/**
 * HelpCollection — Category page with articles list
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { setMetaTags, clearMetaTags, setCanonical, clearCanonical, setJsonLd, clearJsonLd, buildCanonicalUrl, buildBreadcrumbLd } from '../lib/seo';

import { T } from '../styles/tokens';
const SUPPORTED_LOCALES = [
  'pt-BR', 'en', 'es', 'fr', 'de', 'it', 'ja', 'ko', 'zh-CN', 'zh-TW', 'ru',
  'ar', 'hi', 'nl', 'pl', 'tr', 'sv', 'da', 'nb', 'fi',
];

function HreflangTags({ path }: { path: string }) {
  useEffect(() => {
    document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());
    const base = '/help';
    for (const loc of SUPPORTED_LOCALES) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = loc;
      link.href = `${base}/${loc}${path}`;
      link.setAttribute('data-hreflang', 'true');
      document.head.appendChild(link);
    }
    const xd = document.createElement('link');
    xd.rel = 'alternate';
    xd.hreflang = 'x-default';
    xd.href = `${base}/en${path}`;
    xd.setAttribute('data-hreflang', 'true');
    document.head.appendChild(xd);
    return () => {
      document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());
    };
  }, [path]);
  return null;
}

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  view_count: number;
  updated_at: string;
  published_at: string;
  is_release_note: number;
}

export function HelpCollectionPage({ categorySlug, locale: propLocale }: { path?: string; categorySlug?: string; locale?: string }) {
  const locale = propLocale || 'pt-BR';
  const isHelpDomain = typeof window !== 'undefined' && window.location.hostname.startsWith('help.');
  const basePrefix = isHelpDomain ? '' : '/help';
  const [category, setCategory] = useState<any>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!categorySlug) return;
    api.get(`/cms/categories/${categorySlug}?contentType=help&locale=${locale}`).then((res: any) => {
      setCategory(res?.category || null);
      setArticles(res?.entries || res?.articles || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [categorySlug, locale]);

  // SEO: meta tags, canonical, JSON-LD
  useEffect(() => {
    if (!category || !categorySlug) return;
    const url = buildCanonicalUrl(locale, categorySlug);
    setMetaTags({
      title: `${category.name || categorySlug} - Help Center`,
      description: category.description || `Browse articles in ${category.name || categorySlug} - WhiteLabelClaw Help Center`,
      url,
    });
    setCanonical(url);
    setJsonLd([
      buildBreadcrumbLd([
        { name: 'Help Center', url: `/help/${locale}` },
        { name: category.name || categorySlug, url },
      ]),
    ]);
    return () => { clearMetaTags(); clearCanonical(); clearJsonLd(); };
  }, [category, categorySlug, locale]);

  const formatDate = (d: string) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString(locale === 'en' ? 'en-US' : 'pt-BR', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return d; }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <HreflangTags path={`/${categorySlug || ''}`} />
      {/* Top bar */}
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}` }}>
        <a href={`${basePrefix}/${locale}`} style={{ color: T.primary, textDecoration: 'none', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          {locale === 'en' ? 'All categories' : 'Todas as categorias'}
        </a>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 20px 60px' }}>
        {/* Breadcrumb */}
        <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 24, display: 'flex', gap: 6, alignItems: 'center' }}>
          <a href={`${basePrefix}/${locale}`} style={{ color: T.primary, textDecoration: 'none' }}>Help Center</a>
          <span>/</span>
          <span>{category?.name || categorySlug}</span>
        </div>

        {/* Category header */}
        {category && (
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 8px' }}>{category.name}</h1>
            {category.description && <p style={{ fontSize: 15, color: T.textSecondary, margin: 0 }}>{category.description}</p>}
          </div>
        )}

        {/* Articles list */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[0,1,2].map(i => <div key={i} style={{ height: 14, borderRadius: 6, width: `${70-i*15}%`, background: `linear-gradient(90deg, ${T.borderSubtle} 25%, ${T.border} 50%, ${T.borderSubtle} 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.5s ease-in-out infinite', animationDelay: `${i*0.1}s` }} />)}</div>
        ) : articles.length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 14 }}>{locale === 'en' ? 'No articles in this category yet.' : 'Nenhum artigo nesta categoria ainda.'}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 2 }}>
            {articles.map((a) => (
              <a key={a.id} href={`${basePrefix}/${locale}/${categorySlug}/${a.slug}`} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '16px 16px', borderRadius: 8, textDecoration: 'none', color: T.text,
                transition: 'background 0.15s', borderBottom: `1px solid ${T.borderSubtle}`,
              }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{a.title || a.slug}</div>
                  {a.excerpt && <div style={{ fontSize: 13, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{a.excerpt}</div>}
                </div>
                <div style={{ fontSize: 12, color: T.textDisabled, marginLeft: 16, flexShrink: 0 }}>
                  {formatDate(a.published_at || a.updated_at)}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
